import { render } from "preact";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReviewWidget } from "../components/ReviewWidget";
import { PortalProvider } from "../context/PortalContext";
import { widgetEventBus } from "../lib/widgetEvents";
import type { ModalOpenSource } from "../utils/tracking";
import { fetchAndPublishRatingsTotal } from "../utils/ratingsBadge";
import styles from "../index.css?inline";
import "../index.css";

declare global {
  interface Window {
    __SOCIAL_OCEAN_REVIEWS__?: {
      autoOpen?: boolean;
      source?: ModalOpenSource;
    };
  }
}

const WIDGET_HOST_ID = "social-ocean-review-host";
const MOUNT_SELECTOR = "[data-social-ocean-carousel]";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export function mountReviewWidget(target: HTMLElement) {
  // A/B gate: the control arm shows plain stars + native JudgeMe. It must NOT
  // mount the widget or fire reviews.widget_viewed — but it DOES surface the
  // real ratings total on the SHARED theme star badge (parity with treatment;
  // only the count-up animation and the review surface differ).
  // FAIL-OPEN: absent, null, "unbucketed", or "treatment" all mount normally.
  if ((window as any).__SO_AB__?.variant === "control") {
    void fetchAndPublishRatingsTotal();
    return;
  }

  if (document.getElementById(WIDGET_HOST_ID)) {
    return;
  }

  while (target.firstChild) {
    target.removeChild(target.firstChild);
  }

  const host = document.createElement("div");
  host.id = WIDGET_HOST_ID;
  host.style.display = "block";
  host.style.width = "100%";
  host.style.position = "relative";
  host.style.zIndex = "40";

  target.appendChild(host);

  const shadow = host.attachShadow({ mode: "open" });

  const rootWrapper = document.createElement("div");
  rootWrapper.className = "so-widget-root";
  rootWrapper.style.isolation = "isolate";
  shadow.appendChild(rootWrapper);

  try {
    const styleSheet = new CSSStyleSheet();
    styleSheet.replaceSync(styles);
    shadow.adoptedStyleSheets = [styleSheet];
  } catch (e) {
    console.error(
      "Failed to construct CSSStyleSheet, falling back to <style>",
      e,
    );
    const style = document.createElement("style");
    style.textContent = styles;
    shadow.appendChild(style);
  }

  render(
    <QueryClientProvider client={queryClient}>
      <PortalProvider container={rootWrapper}>
        <ReviewWidget />
      </PortalProvider>
    </QueryClientProvider>,
    rootWrapper,
  );

  window.addEventListener("social-ocean:open-reviews", ((
    event: CustomEvent<{ source?: ModalOpenSource; topic?: string }>,
  ) => {
    const source = event.detail?.source ?? "other_external";
    widgetEventBus.emit({ type: "open", source, topic: event.detail?.topic });
  }) as EventListener);

  const cfg = window.__SOCIAL_OCEAN_REVIEWS__;
  if (cfg?.autoOpen) {
    cfg.autoOpen = false;
    widgetEventBus.emit({ type: "open", source: cfg.source ?? "other_external" });
  }
}

if (typeof window !== "undefined") {
  const init = () => {
    if (document.getElementById(WIDGET_HOST_ID)) return;
    const target = document.querySelector<HTMLElement>(MOUNT_SELECTOR);
    if (target) mountReviewWidget(target);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
}
